import { useId } from 'react';
import { Cable, Cloud, Smartphone } from 'lucide-react';
import {
  getAdministrativeDivisionLabel,
  getCountryName,
  type BranchType,
  type PlannedLicenseType,
} from '@quds/shared';
import { CountryDisplay } from '@/components/location';
import {
  Checkbox,
  FormField,
  Input,
  NotLinkedNotice,
  Select,
  fieldDescribedBy,
} from '@/components/ui';
import { formatDate, toDateInputValue } from '@/lib/format';
import { DURATION_LABELS, LICENSE_TYPE_LABELS } from '../components/labels';
import {
  BRANCH_TYPE_LABELS,
  DURATIONS_BY_TYPE,
  computeExpiry,
  durationLabel,
  type BranchErrors,
  type DurationKey,
  type LicenseErrors,
  type WizardState,
} from './wizardModel';

// ---------------------------------------------------------------------------
// Step 2 — Branches & Location
// ---------------------------------------------------------------------------

export function BranchStep({
  branch,
  errors,
  onChange,
  info,
}: {
  branch: WizardState['branch'];
  errors: BranchErrors;
  onChange: (patch: Partial<WizardState['branch']>) => void;
  info: WizardState['info'];
}) {
  const base = useId();
  const divisionLabel = info.country ? getAdministrativeDivisionLabel(info.country) : 'Division';
  const inheritedBits = [
    info.country ? getCountryName(info.country) : null,
    info.administrativeAreaLevel1 || null,
    info.city || null,
  ].filter(Boolean);

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-ink-secondary">
        Every business starts with one main branch. Additional branches can be added later from the business details page.
      </p>

      <div className="rounded-lg border border-line bg-surface-muted px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Inherited location</p>
        <p className="mt-1.5 text-sm text-ink">
          {inheritedBits.length ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              {info.country ? <CountryDisplay country={info.country} city={info.city || undefined} /> : null}
              {info.administrativeAreaLevel1 ? (
                <span className="text-ink-secondary">
                  {divisionLabel}: <span className="text-ink">{info.administrativeAreaLevel1}</span>
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-ink-muted">Set country and city in Business Information first.</span>
          )}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          The main branch inherits country{info.country ? ` / ${divisionLabel.toLowerCase()}` : ''} from the business. You can override branch location later.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Branch Name" htmlFor={`${base}-name`} required error={errors.name} className="sm:col-span-2">
          <Input
            id={`${base}-name`}
            value={branch.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Main Branch"
            invalid={!!errors.name}
            aria-describedby={fieldDescribedBy(`${base}-name`, { error: errors.name })}
          />
        </FormField>
        <FormField
          label="Branch ID"
          htmlFor={`${base}-ref`}
          required
          error={errors.reference}
          hint="Must match the Branch ID configured in the pharmacy POS."
        >
          <Input
            id={`${base}-ref`}
            value={branch.reference}
            onChange={(e) => onChange({ reference: e.target.value.toUpperCase() })}
            placeholder="BR-01"
            maxLength={16}
            className="font-mono uppercase"
            invalid={!!errors.reference}
            aria-describedby={fieldDescribedBy(`${base}-ref`, { hint: true, error: errors.reference })}
          />
        </FormField>
        <FormField label="Branch Type" htmlFor={`${base}-type`}>
          <Select
            id={`${base}-type`}
            value={branch.type}
            onChange={(e) => onChange({ type: e.target.value as BranchType })}
            options={(Object.keys(BRANCH_TYPE_LABELS) as BranchType[]).map((t) => ({ value: t, label: BRANCH_TYPE_LABELS[t] }))}
          />
        </FormField>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — System Setup (Production progressive disclosure)
// ---------------------------------------------------------------------------

export function SystemStep({
  production,
  onProductionChange,
}: {
  production: WizardState['production'];
  onProductionChange: (patch: Partial<WizardState['production']>) => void;
}) {
  const prodOn = production.createProductionEnvironment;
  return (
    <div className="space-y-6">
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-ink">Production environment</legend>
        <Checkbox
          card
          checked={prodOn}
          onChange={(e) => onProductionChange({ createProductionEnvironment: e.target.checked })}
          label={
            <span className="flex items-center gap-2">
              <Cloud size={16} aria-hidden="true" className="text-ink-muted" /> Create Production Environment
            </span>
          }
          description="Creates a real Production tenant and first branch (POS enrollment code generated) and links them to this business."
        />
        {prodOn ? (
          <div className="mt-3 space-y-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-3">
            <p className="text-[13px] text-warning">
              A real Production tenant and branch will be created when you finish. Enrollment codes and any one-time secrets are shown once on success.
            </p>
            <Checkbox
              checked={production.createApiKey}
              onChange={(e) => onProductionChange({ createApiKey: e.target.checked })}
              label="Create an API key for the first branch"
              description="The API key secret is shown once on the success screen."
            />
          </div>
        ) : (
          <NotLinkedNotice title="Central Admin only." className="mt-3">
            The business is saved in Central Admin without any Production tenant, branch or enrollment. You can start Production setup later from the business details page.
          </NotLinkedNotice>
        )}
      </fieldset>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Optional Services (Owner + License planning + requested intents)
// ---------------------------------------------------------------------------

export function OptionalServicesStep({
  services,
  onServicesChange,
  production,
  onProductionChange,
  license,
  licenseErrors,
  onLicenseChange,
}: {
  services: WizardState['services'];
  onServicesChange: (patch: Partial<WizardState['services']>) => void;
  production: WizardState['production'];
  onProductionChange: (patch: Partial<WizardState['production']>) => void;
  license: WizardState['license'];
  licenseErrors: LicenseErrors;
  onLicenseChange: (patch: Partial<WizardState['license']>) => void;
}) {
  const base = useId();
  const prodOn = production.createProductionEnvironment;
  const durations = DURATIONS_BY_TYPE[license.type];
  const perpetual = license.type === 'perpetual';
  const tomorrow = toDateInputValue(new Date(Date.now() + 86_400_000));

  const onTypeChange = (type: PlannedLicenseType) => {
    const duration: DurationKey | '' = DURATIONS_BY_TYPE[type][0] ?? '';
    onLicenseChange({ type, duration, expiresAt: computeExpiry(duration) });
  };

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-ink">Owner mobile account</h3>
        {!prodOn ? (
          <NotLinkedNotice title="Requires Production environment.">
            Turn on Create Production Environment in System Setup to create an owner account during onboarding. You can also add an owner later from business details.
          </NotLinkedNotice>
        ) : (
          <>
            <Checkbox
              checked={production.owner.enabled}
              onChange={(e) => onProductionChange({ owner: { ...production.owner, enabled: e.target.checked } })}
              label="Create an owner mobile account now"
              description="A one-time temporary password is returned on success."
            />
            {production.owner.enabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Owner username" htmlFor="prod-owner-username" required>
                  <Input
                    id="prod-owner-username"
                    value={production.owner.username}
                    onChange={(e) => onProductionChange({ owner: { ...production.owner, username: e.target.value } })}
                  />
                </FormField>
                <FormField label="Owner full name" htmlFor="prod-owner-fullname" required>
                  <Input
                    id="prod-owner-fullname"
                    value={production.owner.fullName}
                    onChange={(e) => onProductionChange({ owner: { ...production.owner, fullName: e.target.value } })}
                  />
                </FormField>
              </div>
            )}
          </>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-ink">Requested services</h3>
        <p className="text-[13px] text-ink-secondary">
          Records what was agreed during onboarding. These toggles do not themselves provision Production.
        </p>
        <div className="grid gap-3">
          <Checkbox
            card
            checked={services.cloud}
            onChange={(e) => onServicesChange({ cloud: e.target.checked })}
            label={
              <span className="flex items-center gap-2">
                <Cloud size={16} aria-hidden="true" className="text-ink-muted" /> Enable Cloud Services
              </span>
            }
            description="Cloud backup and data synchronisation for this business."
          />
          <Checkbox
            card
            checked={services.api}
            onChange={(e) => onServicesChange({ api: e.target.checked })}
            label={
              <span className="flex items-center gap-2">
                <Cable size={16} aria-hidden="true" className="text-ink-muted" /> Enable API Access
              </span>
            }
            description="Allow third-party integrations using API keys."
          />
          <Checkbox
            card
            checked={services.ownerMobile}
            onChange={(e) => onServicesChange({ ownerMobile: e.target.checked })}
            label={
              <span className="flex items-center gap-2">
                <Smartphone size={16} aria-hidden="true" className="text-ink-muted" /> Enable Owner Mobile App
              </span>
            }
            description="Invite the owner to the Quds owner mobile application."
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-ink">License planning</h3>
        <NotLinkedNotice title="Planned license only.">
          This records the planned license. No license is signed or issued until production licensing is linked.
        </NotLinkedNotice>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="License Type" htmlFor={`${base}-type`} required>
            <Select
              id={`${base}-type`}
              value={license.type}
              onChange={(e) => onTypeChange(e.target.value as PlannedLicenseType)}
              options={(Object.keys(LICENSE_TYPE_LABELS) as PlannedLicenseType[]).map((t) => ({
                value: t,
                label: LICENSE_TYPE_LABELS[t],
              }))}
            />
          </FormField>
          <FormField label="License Duration" htmlFor={`${base}-duration`} required={!perpetual} error={licenseErrors.duration}>
            {perpetual ? (
              <Input id={`${base}-duration`} value="No expiration" disabled />
            ) : (
              <Select
                id={`${base}-duration`}
                value={license.duration}
                onChange={(e) => {
                  const duration = e.target.value as DurationKey;
                  onLicenseChange({ duration, expiresAt: computeExpiry(duration) });
                }}
                options={durations.map((d) => ({ value: d, label: DURATION_LABELS[d] }))}
                invalid={!!licenseErrors.duration}
              />
            )}
          </FormField>
          <FormField
            label="Expiration Date"
            htmlFor={`${base}-expires`}
            required={!perpetual}
            error={licenseErrors.expiresAt}
            hint={perpetual ? undefined : 'Calculated from the duration; you can adjust it.'}
            className="sm:col-span-2"
          >
            <Input
              id={`${base}-expires`}
              type={perpetual ? 'text' : 'date'}
              value={perpetual ? 'Never' : license.expiresAt}
              min={tomorrow}
              disabled={perpetual}
              onChange={(e) => onLicenseChange({ expiresAt: e.target.value })}
              invalid={!!licenseErrors.expiresAt}
              aria-describedby={fieldDescribedBy(`${base}-expires`, {
                hint: !perpetual,
                error: licenseErrors.expiresAt,
              })}
            />
          </FormField>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Review (human summary)
// ---------------------------------------------------------------------------

function requested(v: boolean) {
  return v ? 'Requested' : 'Not requested';
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-ink">{value || '—'}</dd>
    </div>
  );
}

export function ReviewStep({ state, onEdit }: { state: WizardState; onEdit: (step: number) => void }) {
  const locationLine = [
    getCountryName(state.info.country),
    state.info.administrativeAreaLevel1 || null,
    state.info.city,
    state.info.area || null,
  ]
    .filter(Boolean)
    .join(' · ');

  const productionLine = state.production.createProductionEnvironment
    ? [
        'Create Production tenant and first branch',
        state.production.createApiKey ? 'with API key' : null,
      ]
        .filter(Boolean)
        .join(' ')
    : 'Central Admin only — no Production tenant';

  const ownerLine =
    state.production.createProductionEnvironment && state.production.owner.enabled
      ? `Create owner (${state.production.owner.username || 'username pending'}${
          state.production.owner.fullName ? ` · ${state.production.owner.fullName}` : ''
        })`
      : 'Not creating an owner account now';

  const groups: { title: string; step: number; rows: [string, string][] }[] = [
    {
      title: 'Business',
      step: 0,
      rows: [
        ['Name', state.info.name],
        ['Location', locationLine],
        ['Contact', `${state.info.contactPerson} · ${state.info.phone}`],
        ['Email', state.info.email],
        ...(state.info.address.trim() ? ([['Address', state.info.address]] as [string, string][]) : []),
        ...(state.info.notes.trim() ? ([['Notes', state.info.notes]] as [string, string][]) : []),
      ],
    },
    {
      title: 'Main branch',
      step: 1,
      rows: [
        ['Branch', `${state.branch.name} (${BRANCH_TYPE_LABELS[state.branch.type]})`],
        ['Branch ID', state.branch.reference.toUpperCase()],
        ['Location', 'Inherits country and division from the business'],
      ],
    },
    {
      title: 'System setup',
      step: 2,
      rows: [['Production', productionLine]],
    },
    {
      title: 'Optional services',
      step: 3,
      rows: [
        ['Owner account', ownerLine],
        ['Cloud services', requested(state.services.cloud)],
        ['API access', requested(state.services.api)],
        ['Owner mobile app', requested(state.services.ownerMobile)],
        ['Planned license', `${LICENSE_TYPE_LABELS[state.license.type]} · ${durationLabel(state)}`],
        [
          'License expiration',
          state.license.type === 'perpetual'
            ? 'Never'
            : formatDate(state.license.expiresAt ? `${state.license.expiresAt}T12:00:00Z` : null),
        ],
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-ink-secondary">
        Review the summary below, then create the business. One-time secrets (if any) appear on the next screen.
      </p>
      {!state.production.createProductionEnvironment && (
        <NotLinkedNotice title="Central Admin only.">
          Creating this business saves the profile and main branch in Central Admin. Nothing is provisioned in Production.
        </NotLinkedNotice>
      )}
      {groups.map((g) => (
        <section key={g.title} className="rounded-lg border border-line">
          <header className="flex items-center justify-between border-b border-line bg-surface-muted px-4 py-2.5">
            <h3 className="text-[13px] font-semibold text-ink">{g.title}</h3>
            <button
              type="button"
              onClick={() => onEdit(g.step)}
              className="rounded text-[13px] font-medium text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
              aria-label={`Edit ${g.title.toLowerCase()}`}
            >
              Edit
            </button>
          </header>
          <dl className="grid gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-2">
            {g.rows.map(([label, value]) => (
              <SummaryRow key={label} label={label} value={value} />
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
